import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { UserPlus } from 'lucide-react';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useAppForm } from '@/lib/form';
import { appFormOptions } from '@/common/form/appFormOptions';
import { requiredInput } from '@/common/form/requiredInput';
import { FormSubmit } from '@/common/components/form-submit/FormSubmit';
import { FormErrors } from '@/common/components/form-errors/FormErrors';
import { Button } from '@/common/components/ui/button';
import { SelectItem } from '@/common/components/ui/select';
import { DialogHeader, DialogTitle, DialogDescription } from '@/common/components/ui/dialog';
import { supplyPointQueryKeys } from '@/features/supply-points/queries';
import { SupplyPointService } from '@/features/supply-points/services/SupplyPointService';
import type { ModalRenderProps } from '@/common/components/modal-provider/types';

const inviteUserSchema = z.object({
  user: requiredInput(z.email()),
  role: z.enum(['full', 'write', 'read']),
});

type Props = Pick<ModalRenderProps<void>, 'resolve'> & {
  supplyPointId: number;
};

export function InviteUserDialogContent({ supplyPointId, resolve }: Props) {
  const queryClient = useQueryClient();

  const form = useAppForm(
    appFormOptions({
      schema: inviteUserSchema,
      defaultValues: { user: null, role: 'read' as const },
      onSubmit: async ({ value }) => {
        await SupplyPointService.createAccess(supplyPointId, {
          user: value.user,
          role: value.role,
        });
        await queryClient.invalidateQueries({ queryKey: supplyPointQueryKeys.all });
        resolve();
      },
    })
  );

  return (
    <>
      <DialogHeader>
        <div className="flex items-center gap-3 mb-1">
          <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10">
            <UserPlus size={20} className="text-primary" />
          </div>
          <div>
            <DialogTitle>
              <Trans>Invite user</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>Sends an email invitation to access this supply point.</Trans>
            </DialogDescription>
          </div>
        </div>
      </DialogHeader>

      <form.AppForm>
        <form.FormElement>
          <FormErrors />

          <div className="space-y-4">
            <form.AppField name="user">
              {(field) => (
                <field.TextField
                  label={<Trans>Email address</Trans>}
                  placeholder={t`user@company.com`}
                  type="email"
                  required
                />
              )}
            </form.AppField>

            <form.AppField name="role">
              {(field) => (
                <field.SelectField label={<Trans>Role</Trans>} required>
                  <SelectItem value="full">
                    <Trans>Admin — full access</Trans>
                  </SelectItem>
                  <SelectItem value="write">
                    <Trans>Editor — read and write</Trans>
                  </SelectItem>
                  <SelectItem value="read">
                    <Trans>Reader — read only</Trans>
                  </SelectItem>
                </field.SelectField>
              )}
            </form.AppField>

            <div className="rounded-xl bg-primary/5 border border-primary/10 px-4 py-3 text-sm text-primary/80">
              <Trans>The user will receive an email invitation to access this supply point.</Trans>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" type="button" onClick={() => resolve()}>
              <Trans>Cancel</Trans>
            </Button>
            <FormSubmit>
              <Trans>Send invitation</Trans>
            </FormSubmit>
          </div>
        </form.FormElement>
      </form.AppForm>
    </>
  );
}
